import {Button, ButtonGroup, Dialog} from 'platform/components';
import {Box, Heading, Show, Space, Text, Video, Image} from 'platform/foundation';

import i18n from '@dms/i18n';

import {pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

interface PromoFeaturePopupProps extends RequiredTestIdProps {
  isOpen: boolean;
  onClose: () => void;
  moreInfoUrl?: string;
}

const MEDIA_URL = 'https://storage.api.omnetic.com/public/feature-promo.webp';
const CHRISTMAS_VIDEO_URL = 'https://www.youtube.com/watch?v=oklIxQIGOyg';

const acceptedVideoExtensions = ['mp4'];
const acceptedImageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

const Media = (props: {url: string} & RequiredTestIdProps) => {
  const isVideo = acceptedVideoExtensions.some((extension) => props.url.includes(extension));
  const isImage = acceptedImageExtensions.some((extension) => props.url.includes(extension));

  if (isVideo) {
    return (
      <Video
        src={props.url}
        data-testid={suffixTestId('promo-feature-video', props)}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        shouldHideControls
      />
    );
  }

  if (isImage) {
    return (
      <Image
        src={props.url}
        data-testid={suffixTestId('promo-feature-image', props)}
        fit="cover"
        height={100}
      />
    );
  }

  return null;
};

export function PromoFeaturePopup(props: PromoFeaturePopupProps) {
  const {isOpen, onClose} = props;

  const handleGotItClick = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'ad_feature_dialog_button_got_it_click',
    });
    onClose();
  };

  const handleFindOutMoreClick = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'ad_feature_dialog_button_find_out_more_click',
    });
    window.open(CHRISTMAS_VIDEO_URL, '_blank');
  };

  const handleClose = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'ad_feature_dialog_button_close_click',
    });
    onClose();
  };

  return (
    <Dialog
      isOpen={isOpen}
      title={i18n.t('ad.feature.dialog.title')}
      onClose={handleClose}
      data-testid={props['data-testid']}
    >
      <Box height={108} paddingVertical={4}>
        <Media url={MEDIA_URL} data-testid={props['data-testid']} />
      </Box>

      <Heading size={3}>{i18n.t('ad.feature.dialog.heading')}</Heading>
      <Space vertical={4} />
      <Text color="secondary">{i18n.t('ad.feature.dialog.description')}</Text>
      <Space vertical={4} />
      <ButtonGroup align="right">
        <Show when={props.moreInfoUrl}>
          <Button
            variant="link"
            onClick={handleFindOutMoreClick}
            title={i18n.t('ad.feature.dialog.button.findOutMore')}
          />
        </Show>
        <Button
          variant="primary"
          onClick={handleGotItClick}
          title={i18n.t('ad.feature.dialog.button.gotIt')}
        />
      </ButtonGroup>
    </Dialog>
  );
}
